// CORE
import React from 'react'
import { observer } from 'mobx-react'
import LoadingData from 'components/ProgressIndicator/LoadingData'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import ToolTip from '@material-ui/core/Tooltip'

// COMPONENTS
import CustomTable from '../../Table/Table'
import Card from '../../Cards/Card'
import CallDetailsDrawer from 'components/CallPro/SideDrawer/CallDetailsDrawer/CallDetailsDrawer'
import CallDetailsWrapper from 'components/CallPro/SideDrawer/CallDetailsWrapper/CallDetailsWrapper'
import DownloadCallsButton from 'components/CallPro/DownloadCallsButton/DownloadCallsButton'
import CallTableFilters from 'components/CallPro/CallTableFilters/CallTableFilters'

// UTILITIES
import useSidePanel from '../../../helpers/hooks/useSidePanel'

// STYLES
import { useStyles } from './OpportunityCalls.style'

// STORE
import { useStores } from 'config/store'

// CONFIGS
import {
  callProCallsColumns,
  renderCallProDashboardColumns,
} from 'components/CallPro/Tables/CallsTableColumns'

// INTERFACES
import { Theme, useMediaQuery } from '@material-ui/core'
import Account from 'models/Account'

const OpportunityCalls = observer(() => {
  const classes = useStyles()
  const { callsStore, accountStore } = useStores()
  const { drawerOpen, handleCloseDrawer, handleSelectCall } = useSidePanel()
  const mobileDevice = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  )
  const account: Account = accountStore.accounts[0]

  const onColumnSort = (key: string) => {
    callsStore.toggleSorting(key)
    callsStore.refreshCalls()
  }

  const onChangePage = (page: number) => {
    callsStore.setPaginationPage(page)
    callsStore.refreshCalls()
  }

  const onChangePageSize = (pageSize: number) => {
    callsStore.setPaginationSize(pageSize)
    callsStore.refreshCalls()
  }

  const {
    loadingStates,
    pagination,
    opportunityCallsPerPage,
    opportunityCallsTotalCount,
  } = callsStore

  return (
    <>
      <Card
        title={
          <div className={classes.titleWrapper}>
            <ToolTip
              id="all-calls"
              title='These are the calls we scored as valid appointment opportunities, including potential opportunities from missed calls, unanswered calls, and voicemails that require follow-up.'
              placement="bottom-start"
              classes={{
                tooltip: classes.tooltip,
              }}
            >
              <div>
                <span>Opportunity Calls</span>
                {!mobileDevice ? (
                  <InfoOutlinedIcon className={classes.grade_icon} />
                ) : null}
              </div>
            </ToolTip>
          </div>
        }
        action={<DownloadCallsButton callCount={opportunityCallsTotalCount} />}
        classes={{
          root: classes.cardRoot,
          content: classes.cardContent,
        }}
      >
        <CallTableFilters />
        <CustomTable
          columns={callProCallsColumns(account)}
          rows={renderCallProDashboardColumns({
            calls: opportunityCallsPerPage,
            currentPage: pagination.page,
            pageSize: pagination.pageSize,
            stopAudio: false,
          })}
          defaultRowPerPage={pagination.pageSize}
          isLoading={loadingStates.opportunityCall}
          orderBy={pagination.sort}
          dir={pagination.dir}
          onColumnSort={onColumnSort}
          onChangePageSize={onChangePageSize}
          onChangePage={onChangePage}
          totalRowsCount={opportunityCallsTotalCount}
          currentPage={pagination.page}
          handleSelectCall={handleSelectCall}
        />
        <LoadingData hidden={!loadingStates.opportunityCall} />
      </Card>
      <CallDetailsDrawer
        title="Call Details"
        drawerOpen={drawerOpen}
        handleCloseDrawer={handleCloseDrawer}
      >
        <CallDetailsWrapper />
      </CallDetailsDrawer>
    </>
  )
})

export default OpportunityCalls
